import moment from "moment";
import { type AssetTokenType, type NetworkType } from "../models/app-config";
import {
  DescriptionTranslationKey,
  type RevenueHistoryType,
  TxLedgerEntryType,
} from "../models/secure-chat/audience";
import { convertSecondsToDuration } from "./date";
import { type UserInfoType } from "../models/profile/user-info";
import { type RoomInfoType } from "../models/secure-chat";

/**
 * Get the first active network from audience subscription networks array
 * @param networks - Array of networks
 * @returns First active network or first network if no active filter, or null
 */
const getFirstAudienceSubscriptionNetwork = (
  networks: NetworkType[] | null
): NetworkType | null => {
  if (!networks || networks.length === 0) return null;
  // Return first active network, or first network if no active filter
  return networks.find((n) => (n as any).is_active) ?? networks[0] ?? null;
};

/**
 * Get network by chain_id from audience subscription networks array
 * @param networks - Array of networks
 * @param chainId - Chain ID to find
 * @returns Network matching chain_id or null
 */
const getAudienceSubscriptionNetworkByChainId = (
  networks: NetworkType[] | null,
  chainId: number | string
): NetworkType | null => {
  if (!networks || networks.length === 0) return null;
  return networks.find((n) => Number(n.chain_id) === Number(chainId)) ?? null;
};

/**
 * Get the first active token from audience subscription tokens object
 * @param tokens - Object mapping chain_id to token arrays
 * @returns First active token from first chain, or null
 */
const getFirstAudienceSubscriptionToken = (
  tokens: Record<string, AssetTokenType[]> | null
): AssetTokenType | null => {
  if (!tokens) return null;
  const firstChainTokens = Object.values(tokens)?.[0];
  if (!firstChainTokens || firstChainTokens.length === 0) return null;
  // Return first active token, or first token if no active filter
  return (
    firstChainTokens.find((t) => t.is_active) ?? firstChainTokens[0] ?? null
  );
};

/**
 * Get token by chain_id and optionally token contract address
 * @param tokens - Object mapping chain_id to token arrays
 * @param chainId - Chain ID to find tokens for
 * @param contractAddress - Optional contract address to find specific token
 * @returns Token matching criteria or first active token from chain, or null
 */
const getAudienceSubscriptionTokensByChainId = (
  tokens: Record<string, AssetTokenType[]> | null,
  chainId: number | string
): AssetTokenType[] => {
  if (!tokens) return [];
  const chainTokens = tokens[String(chainId)];
  return chainTokens;
};

/**
 * Filter only active assets from audience_subscription_assets
 * @param assets - Object mapping chain_id to asset arrays
 * @returns Object with only active assets, or null if no assets provided
 */
const filterActiveAudienceSubscriptionAssets = (
  assets: Record<string, AssetTokenType[]> | null | undefined
): Record<string, AssetTokenType[]> | null => {
  if (!assets) return null;

  return Object.keys(assets).reduce<Record<string, AssetTokenType[]>>(
    (acc, chainId) => {
      const chainAssets = assets[chainId];
      if (Array.isArray(chainAssets)) {
        const activeAssets = chainAssets.filter(
          (item) => item.is_active === true
        );
        if (activeAssets.length > 0) {
          acc[chainId] = activeAssets;
        }
      }
      return acc;
    },
    {}
  );
};

const getRevenueHistoryInfo = (
  history: RevenueHistoryType,
  t: any,
  durationPlans?: Array<{ duration: number; en: string; ja: string }>,
  locale?: string
): {
  time: string;
  amount: number;
  description: string;
  userInfo: UserInfoType | null;
  partyInfo: RoomInfoType | null;
  transactionHash: string;
  amountColor: string;
  tokenName: string;
  tokenImageUrl: string;
} => {
  const time = moment(history?.created_at).format("LT");
  const userSubscription = history?.user_subscription;
  const toUserInfo = history?.to_user;
  const fromUserInfo = history?.from_user;
  const user = userSubscription?.user;
  const subscriptionPlan = userSubscription?.subscription_plan;
  const partyInfo = subscriptionPlan?.room ?? null;
  const transactionHash = history?.tx_hash;
  const formattedAmount = Number(history?.amount);
  const isServiceFeePaid =
    history?.ledger_entry_type === TxLedgerEntryType.SERVICE_FEE_PAID;
  const isSubscriptionEarned =
    history?.ledger_entry_type === TxLedgerEntryType.SUBSCRIPTION_EARNED;

  const isReferralCommissionPaid =
    history?.ledger_entry_type === TxLedgerEntryType.REFERRAL_COMMISSION_PAID;

  const amount =
    isServiceFeePaid || (toUserInfo && formattedAmount > 0)
      ? formattedAmount * -1
      : formattedAmount;
  const userInfo = toUserInfo ?? fromUserInfo ?? user ?? null;

  const duration = subscriptionPlan?.duration;
  const durationText =
    duration && isSubscriptionEarned
      ? " - " +
        convertSecondsToDuration(Number(duration), t, durationPlans, locale)
      : "";

  const description =
    t(
      DescriptionTranslationKey[history?.ledger_entry_type as TxLedgerEntryType]
    ) + durationText;

  const amountColor =
    amount < 0 || isReferralCommissionPaid
      ? "text-red-delete"
      : "text-green-success1";

  const tokenName = history?.token?.symbol ?? "";
  const tokenImageUrl = history?.token?.image_url ?? "";

  return {
    time,
    amount,
    description,
    userInfo,
    partyInfo,
    transactionHash,
    amountColor,
    tokenName,
    tokenImageUrl,
  };
};

enum DateVariant {
  UNTIL = "until",
  REMAINING = "remaining",
  EXPIRED = "expired",
}

const getExpiryLabelByDate = (
  date: number,
  t: any
): {
  date: string;
  variant: DateVariant;
} => {
  const endDate = moment.unix(date);
  const now = moment();
  const secondsRemaining = endDate.diff(now, "seconds");
  const daysRemaining = endDate.diff(now, "days");

  if (daysRemaining >= 3) {
    return {
      date: t("web.until_x_date_expired", {
        x: endDate.format("YYYY-MM-DD"),
      }),
      variant: DateVariant.UNTIL,
    };
  } else if (daysRemaining > 0 && daysRemaining < 3) {
    return {
      date: t("web.x_days_remaining", { x: daysRemaining }),
      variant: DateVariant.REMAINING,
    };
  } else if (daysRemaining === 0 && secondsRemaining > 0) {
    return {
      date: t("expires_today"),
      variant: DateVariant.EXPIRED,
    };
  } else {
    return {
      date: t("expired"),
      variant: DateVariant.EXPIRED,
    };
  }
};

/**
 * Check if a subscription plan is a Lifetime plan based on end_date
 * Lifetime plan has duration = 3155760000 seconds (approximately 100 years)
 * @param endDate - End date timestamp in seconds (Unix timestamp)
 * @returns true if the plan is a Lifetime plan, false otherwise
 */
const isLifetimePlan = (endDate: number | null | undefined): boolean => {
  if (!endDate) return false;

  const lifetimeThresholdDate = moment().add(100, "years");
  const endDateMoment = moment(Number(endDate) * 1000);

  return endDateMoment.isSameOrAfter(lifetimeThresholdDate);
};

export {
  getFirstAudienceSubscriptionNetwork,
  getAudienceSubscriptionNetworkByChainId,
  getFirstAudienceSubscriptionToken,
  getAudienceSubscriptionTokensByChainId,
  getRevenueHistoryInfo,
  getExpiryLabelByDate,
  isLifetimePlan,
  filterActiveAudienceSubscriptionAssets,
  DateVariant,
};
